import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'

import { removeLogicAndTruncate } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { wrapTooltip } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/tooltipFormatter'

const buildTooltipDesc = ({ amount, percents, position, text, unitLabel, ignoreRefs, ignorePercents, percentDecimals }) => {
  const percentsHtml = ignorePercents ? '' : `<div><b>${percents.toFixed(percentDecimals ?? 1)}</b>%</div>`
  const ref = ignoreRefs ? '' : `A${position}: `
  const truncatedText = removeLogicAndTruncate(text, 100)

  if (unitLabel === '') {
    return `${percentsHtml}<div class='highcharts-tooltip-text'><b>${ref}</b> ${truncatedText}</div>`
  }

  const label = unitLabel ?? pluralize(Number(amount), 'respondent', 'respondents')
  const respInfo = `<b>${amount}</b> ${label}`
  return `${percentsHtml}<div class='highcharts-tooltip-text'>${respInfo}</div><div><b>${ref}</b> ${truncatedText}</div>`
}

export const calcTreeData = (answerVariants, answer, { colorTheme, ignoreRefs, ignorePercents, percentDecimals }) => {
  const avsCopy = answerVariants ? [...answerVariants] : []

  let colorIndex = 0

  const calc = (avs = [], as = {}, scope = []) =>
    avs.flatMap(av => {
      if (!av) return []

      const key = `a${av.position}`
      const amount = formatAmounts(as[key]?.amount || 0)
      const percents = as[key]?.percents || 0

      const desc = buildTooltipDesc({
        percentDecimals,
        amount,
        percents,
        position: av.position,
        text: av.text,
        unitLabel: as.unitLabel,
        ignoreRefs,
        ignorePercents
      })

      const id = [...scope, av.position]

      if (!scope.length) colorIndex++

      const color = !scope.length ? colorTheme[(colorIndex - 1) % colorTheme.length] : undefined

      const parent = scope.length ? scope.join('.') : undefined
      const isLast = !av.answerVariants?.length

      return [[id.join('.'), parent, av.text, percents, color, desc, isLast], ...calc(av.answerVariants, as[key], id)]
    })

  return calc(avsCopy, answer || {})
}

export const buildBreadcrumbsConfig = () => ({
  buttonTheme: {
    color: '#29abe2',
    states: {
      disabled: { fill: 'none', stroke: 'none', style: { fontWeight: 'normal', cursor: 'default' } },
      hover: { fill: 'none', stroke: 'none', style: { fontWeight: 'normal' } },
      select: { fill: 'none', stroke: 'none', style: { fontWeight: 'normal' } }
    },
    style: {
      color: '#29abe2',
      fontFamily: 'Ubuntu, Calibri, Segoe, Helvetica, Arial',
      fontSize: '12px',
      fontWeight: 'normal',
      lineHeight: '14px',
      textOverflow: 'ellipsis',
      textOutline: 'none'
    }
  },
  color: '#000000',
  formatter: ({ levelOptions }) =>
    levelOptions.name.length > 30 ? levelOptions.name.slice(0, 27) + '...' : levelOptions.name,
  style: {
    color: '#000000',
    fontFamily: 'Ubuntu, Calibri, Segoe, Helvetica, Arial',
    fontSize: '12px',
    fontWeight: 'normal',
    lineHeight: '14px',
    textOverflow: 'ellipsis',
    textOutline: 'none'
  }
})

export const createSetRootNodeHandler = setRootNode => {
  let timer = null
  return event => {
    // Only a researcher drilling is worth coalescing; the chart's own reset carries no trigger and
    // has to be seen at once, or a burst of clicks swallows it and it is never answered.
    if (!event.trigger) return setRootNode(event)

    if (timer) clearTimeout(timer)
    timer = setTimeout(() => setRootNode(event), 300)
  }
}

export const treeTooltipFormatter = function () {
  return this.point.desc === '' ? false : wrapTooltip(this.point.desc)
}

export const recolorTreeData = (data, colorTheme) => {
  let colorIndex = 0
  return data.map(v => {
    if (v[1] !== undefined) return v
    colorIndex++
    const copy = [...v]
    copy[4] = colorTheme[(colorIndex - 1) % colorTheme.length]
    return copy
  })
}
